import type { TechnicianBlockedTime } from '@/types/scheduler';

export interface BlockedLabel {
  /** Short chip text (the time-off type, or a category). */
  label: string;
  /** True when the underlying reason is private (SICK) and must stay generic. */
  sensitive: boolean;
}

/**
 * NOT-330 — derive a tile chip label from a technician blocked-time.
 *
 * TIME_OFF is ALWAYS privacy-sensitive: the approval flow stores
 * `request.reason ?? "Time off (<TYPE>)"`, so when an employee gives a freeform
 * reason (e.g. a sick day noted "doctor appointment") the raw reason — not a
 * parseable type — is what's persisted. We therefore never surface a TIME_OFF
 * reason in tooltips; we only show the structured `(TYPE)` as a label when it's
 * present and non-SICK, otherwise the generic "Time off". Other sources are
 * operational (not medical), so their reason is safe to show.
 */
export function blockedTimeLabel(
  block: Pick<TechnicianBlockedTime, 'source' | 'reason'>,
): BlockedLabel {
  const reason = block.reason ?? '';
  switch (block.source) {
    case 'TIME_OFF': {
      const type = reason.match(/\(([^)]+)\)/)?.[1]?.trim();
      if (type && type.toUpperCase() !== 'SICK') return { label: type, sensitive: true };
      return { label: 'Time off', sensitive: true };
    }
    case 'HOLIDAY':
      return { label: 'Holiday', sensitive: false };
    case 'EXTERNAL_CALENDAR':
      return { label: 'Busy', sensitive: false };
    case 'MANUAL':
    default:
      return { label: reason || 'Blocked', sensitive: false };
  }
}

/** Full hover text — the raw reason, unless it's sensitive (then the generic label). */
export function blockedTimeTooltip(
  block: Pick<TechnicianBlockedTime, 'source' | 'reason'>,
): string {
  const { label, sensitive } = blockedTimeLabel(block);
  return sensitive ? label : block.reason || label;
}

/** True when a blocked-time overlaps the given calendar day (YYYY-MM-DD) in tz. */
export function blockedTimeCoversDay(
  block: Pick<TechnicianBlockedTime, 'startAt' | 'endAt'>,
  dayKey: string,
  dayKeyOf: (iso: string) => string,
): boolean {
  const start = dayKeyOf(block.startAt);
  const end = dayKeyOf(block.endAt);
  return start <= dayKey && dayKey <= end;
}
